import type { MapCanvasValue } from "../components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import {
  DEFAULT_FURNITURE_FILL_COLOR,
  DEFAULT_WALL_COLOR
} from "../components/map/defines";
import {
  type CircleShape,
  type RectangleShape,
  type Shape,
  SpotShapeType
} from "../types";
import { calibrateImportedDesks } from "./calibrateImportedDesks";
import { fillImportedPillars } from "./fillImportedPillars";
import { normalizeImportedWallWidths } from "./normalizeImportedWallWidths";

type Primitive = RectangleShape | CircleShape;
const primitive = (s: Shape): s is Primitive =>
  s.type === "rectangle" || s.type === "circle";
const n = (value: number) => Math.round(value * 1000) / 1000;
const angle = (s: Shape) => ((s.rotation || 0) * Math.PI) / 180;
function origin(s: Primitive) {
  const a = angle(s);
  return s.type === "circle"
    ? {
        x: s.x - (s.width / 2) * Math.cos(a) + (s.height / 2) * Math.sin(a),
        y: s.y - (s.width / 2) * Math.sin(a) - (s.height / 2) * Math.cos(a)
      }
    : { x: s.x, y: s.y };
}
function center(s: Primitive) {
  const o = origin(s),
    a = angle(s);
  return {
    x: o.x + (s.width / 2) * Math.cos(a) - (s.height / 2) * Math.sin(a),
    y: o.y + (s.width / 2) * Math.sin(a) + (s.height / 2) * Math.cos(a)
  };
}
export function importedTableBounds(s: Primitive) {
  const o = origin(s),
    a = angle(s);
  const points = [
    [0, 0],
    [s.width, 0],
    [0, s.height],
    [s.width, s.height]
  ].map(([x, y]) => ({
    x: o.x + x * Math.cos(a) - y * Math.sin(a),
    y: o.y + x * Math.sin(a) + y * Math.cos(a)
  }));
  return {
    x: Math.min(...points.map((p) => p.x)),
    y: Math.min(...points.map((p) => p.y)),
    right: Math.max(...points.map((p) => p.x)),
    bottom: Math.max(...points.map((p) => p.y))
  };
}
export const importedTables = (map: MapCanvasValue) =>
  map.shapes.filter(
    (s): s is Primitive => primitive(s) && s.layerLabel === "table"
  );

/** Deterministic SVG only: no model markup, embedded links, scripts or labels. */
export function compileImportFurniture(
  map: MapCanvasValue,
  deskIds: ReadonlySet<string> = new Set()
): MapCanvasValue {
  const tables = importedTables(map);
  // AI-recognized native desks already include their tabletop and chair glyph.
  // Reconstruct their image-space tabletop only for ownership/duplicate checks.
  const nativeDesks: RectangleShape[] = map.shapes.flatMap((s) => {
    if (s.type !== "spot" || s.spotType !== "desk") return [];
    const a = angle(s),
      sy = s.scaleY ?? 1;
    return [
      {
        id: s.id,
        type: "rectangle",
        layerType: "shapes",
        layerLabel: "table",
        x: s.x - (s.orientation === "bottom" ? 10 : 40) * sy * Math.sin(a),
        y: s.y + (s.orientation === "bottom" ? 10 : 40) * sy * Math.cos(a),
        width: 90 * (s.scaleX ?? 1),
        height: 50 * sy,
        rotation: s.rotation
      }
    ];
  });
  const sameTabletop = (table: Primitive, desk: Primitive) => {
    const ratio = (table.width * table.height) / (desk.width * desk.height);
    if (ratio < 0.8 || ratio > 1.25) return false;
    const o = origin(table),
      d = origin(desk),
      a = angle(table),
      b = angle(desk);
    const tolerance = Math.max(1, Math.min(desk.width, desk.height) * 0.05);
    return [
      [0, 0],
      [table.width, 0],
      [0, table.height],
      [table.width, table.height]
    ].every(([x, y]) => {
      const dx = o.x + x * Math.cos(a) - y * Math.sin(a) - d.x;
      const dy = o.y + x * Math.sin(a) + y * Math.cos(a) - d.y;
      const localX = dx * Math.cos(b) + dy * Math.sin(b);
      const localY = -dx * Math.sin(b) + dy * Math.cos(b);
      return (
        localX >= -tolerance &&
        localX <= desk.width + tolerance &&
        localY >= -tolerance &&
        localY <= desk.height + tolerance
      );
    });
  };
  const owned = new Set<string>();
  const seenChairs: Primitive[] = [];
  for (const chair of map.shapes.filter(
    (s): s is Primitive => primitive(s) && s.layerLabel === "chair"
  )) {
    const c = center(chair);
    if (
      seenChairs.some((other) => {
        const d = center(other);
        const ratio =
          (chair.width * chair.height) / (other.width * other.height);
        return (
          ratio >= 0.8 &&
          ratio <= 1.25 &&
          Math.hypot(c.x - d.x, c.y - d.y) <
            Math.min(chair.width, chair.height, other.width, other.height) *
              0.25
        );
      })
    )
      owned.add(chair.id);
    else seenChairs.push(chair);
  }

  const deskAnchors: Primitive[] = [...nativeDesks];
  for (const table of tables.filter((t) => deskIds.has(t.id))) {
    if (deskAnchors.some((d) => sameTabletop(table, d))) owned.add(table.id);
    else deskAnchors.push(table);
  }
  for (const table of tables.filter((t) => !deskIds.has(t.id))) {
    if (deskAnchors.some((d) => sameTabletop(table, d))) owned.add(table.id);
  }
  const anchors = [...tables.filter((t) => !owned.has(t.id)), ...nativeDesks];
  const nativeIds = new Set(nativeDesks.map((d) => d.id));
  const isDesk = (table: Primitive) =>
    nativeIds.has(table.id) || deskIds.has(table.id);
  const groups = new Map(anchors.map((t) => [t.id, [t]]));
  for (const chair of map.shapes.filter(
    (s): s is Primitive => primitive(s) && s.layerLabel === "chair"
  )) {
    if (owned.has(chair.id)) continue;
    const c = center(chair);
    const candidates = anchors
      .map((table) => {
        const o = origin(table),
          a = angle(table);
        const x = (c.x - o.x) * Math.cos(a) + (c.y - o.y) * Math.sin(a);
        const y = -(c.x - o.x) * Math.sin(a) + (c.y - o.y) * Math.cos(a);
        const distance = Math.hypot(
          Math.max(0, -x, x - table.width),
          Math.max(0, -y, y - table.height)
        );
        return { table, distance };
      })
      .filter(
        (candidate) =>
          candidate.distance <= Math.max(chair.width, chair.height) * 1.25
      )
      .sort((a, b) => a.distance - b.distance);
    if (!candidates.length) continue;
    const nearest = candidates.filter(
      (c) => c.distance - candidates[0].distance < 1
    );
    if (nearest.length > 1) {
      // No need to assign a chair to one particular desk when every possible
      // owner supplies its own native glyph. Mixed table/desk ties stay visible.
      if (nearest.every((c) => isDesk(c.table))) owned.add(chair.id);
      continue;
    }
    groups.get(candidates[0].table.id)!.push(chair);
    owned.add(chair.id);
  }
  // Seat chairs against the round tabletop, rather than preserving noisy AI
  // center distances. Only regularize angles when a complete, roughly even ring
  // is already observed: never invent seats or redistribute a partial arc.
  for (const [id, parts] of groups) {
    const table = parts[0];
    if (
      table.type !== "circle" ||
      Math.abs(table.width / table.height - 1) > 0.1 ||
      isDesk(table)
    )
      continue;
    const c = center(table);
    const seats = parts
      .slice(1)
      .filter((p) => p.type === "rectangle")
      .map((part) => {
        const p = center(part);
        return { id: part.id, angle: Math.atan2(p.y - c.y, p.x - c.x) };
      })
      .sort((a, b) => a.angle - b.angle);
    const step = (2 * Math.PI) / seats.length;
    const phase =
      seats.reduce((sum, s, i) => sum + s.angle - i * step, 0) / seats.length;
    const regular =
      seats.length >= 3 &&
      seats.every(
        (s, i) =>
          Math.abs(s.angle - phase - i * step) <=
          Math.min(Math.PI / 15, step * 0.2)
      );
    const angles = new Map(
      seats.map((s, i) => [s.id, regular ? phase + i * step : s.angle])
    );
    groups.set(
      id,
      parts.map((part, i) => {
        if (!i || part.type !== "rectangle") return part;
        const theta = angles.get(part.id)!;
        const dx = Math.cos(theta),
          dy = Math.sin(theta);
        const tableAngle = angle(table);
        const localAngle = theta - tableAngle;
        const radius =
          1 /
          Math.hypot(
            Math.cos(localAngle) / (table.width / 2),
            Math.sin(localAngle) / (table.height / 2)
          );
        const gap = Math.min(part.width, part.height) * 0.06;
        const distance = radius + part.height / 2 + gap;
        const p = { x: c.x + dx * distance, y: c.y + dy * distance };
        const rotation = ((theta * 180) / Math.PI + 270) % 180;
        const a = (rotation * Math.PI) / 180;
        return {
          ...part,
          rotation,
          x:
            p.x -
            (part.width / 2) * Math.cos(a) +
            (part.height / 2) * Math.sin(a),
          y:
            p.y -
            (part.width / 2) * Math.sin(a) -
            (part.height / 2) * Math.cos(a)
        };
      })
    );
  }
  // Compare arrangements before generating SVG markup. Small detection noise is
  // normalized; different footprints, part counts and layouts remain distinct.
  const describe = (parts: Primitive[]) => {
    const boxes = parts.map(importedTableBounds);
    const x = Math.min(...boxes.map((b) => b.x)) - 1;
    const y = Math.min(...boxes.map((b) => b.y)) - 1;
    const width = Math.max(...boxes.map((b) => b.right)) - x + 1;
    const height = Math.max(...boxes.map((b) => b.bottom)) - y + 1;
    const features = parts
      .map((p) => {
        const o = origin(p);
        return {
          kind: `${p.layerLabel}:${p.type}`,
          x: (o.x - x) / width,
          y: (o.y - y) / height,
          width: p.width / width,
          height: p.height / height,
          radius: (p.cornerRadius || 0) / Math.min(width, height),
          rotation: p.rotation || 0
        };
      })
      .sort((a, b) => a.kind.localeCompare(b.kind) || a.y - b.y || a.x - b.x);
    return { x, y, width, height, features };
  };
  type Arrangement = ReturnType<typeof describe> & {
    parts: Primitive[];
    id: string;
    count: number;
  };
  const clusters = new Map<string, Arrangement[]>();
  const arrangements = new Map<
    string,
    { instance: ReturnType<typeof describe>; template: Arrangement }
  >();
  for (const s of map.shapes) {
    if (
      !primitive(s) ||
      owned.has(s.id) ||
      deskIds.has(s.id) ||
      !["table", "chair", "cabinet"].includes(s.layerLabel || "")
    )
      continue;
    const parts = groups.get(s.id) || [s];
    const instance = describe(parts);
    const key = instance.features.map((f) => f.kind).join("|");
    const bucket = clusters.get(key) || [];
    const close = (a: number, b: number, tolerance: number) =>
      Math.abs(a - b) <= tolerance;
    let template = bucket.find(
      (t) =>
        Math.max(
          t.width / instance.width,
          instance.width / t.width,
          t.height / instance.height,
          instance.height / t.height
        ) <= 1.12 &&
        Math.abs(t.width / t.height / (instance.width / instance.height) - 1) <=
          0.05 &&
        t.features.every((f, i) => {
          const g = instance.features[i];
          return (
            close(f.x, g.x, 0.025) &&
            close(f.y, g.y, 0.025) &&
            close(f.width, g.width, 0.025) &&
            close(f.height, g.height, 0.025) &&
            close(f.radius, g.radius, 0.015) &&
            Math.abs(((f.rotation - g.rotation + 540) % 360) - 180) <= 3
          );
        })
    );
    if (!template) {
      template = { ...instance, parts, id: s.id, count: 0 };
      bucket.push(template);
      clusters.set(key, bucket);
    }
    template.count++;
    arrangements.set(s.id, { instance, template });
  }
  const assets = new Map<
    string,
    {
      id: string;
      name: string;
      url: string;
      category: string;
      width: number;
      height: number;
    }
  >();
  const furniture = (parts: Primitive[], id: string, label: string): Shape => {
    const boxes = parts.map(importedTableBounds);
    const x = Math.min(...boxes.map((b) => b.x)) - 1,
      y = Math.min(...boxes.map((b) => b.y)) - 1;
    const width = Math.max(...boxes.map((b) => b.right)) - x + 1,
      height = Math.max(...boxes.map((b) => b.bottom)) - y + 1;
    const markup = parts
      .map((s) => {
        const o = origin(s);
        const element =
          s.type === "circle"
            ? `<ellipse cx="${n(s.width / 2)}" cy="${n(s.height / 2)}" rx="${n(s.width / 2)}" ry="${n(s.height / 2)}"/>`
            : `<rect width="${n(s.width)}" height="${n(s.height)}" rx="${n(s.cornerRadius || 0)}"/>`;
        return `<g transform="translate(${n(o.x - x)} ${n(o.y - y)}) rotate(${n(s.rotation || 0)})">${element}</g>`;
      })
      .join("");
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${n(width)}" height="${n(height)}" viewBox="0 0 ${n(width)} ${n(height)}"><g fill="currentColor" stroke="#9CA3AF" stroke-width="1">${markup}</g></svg>`;
    let asset = assets.get(svg);
    if (!asset) {
      asset = {
        id: `import-furniture-${id}`,
        name: label,
        url: `data:image/svg+xml,${encodeURIComponent(svg)}`,
        category: "Imported furniture",
        width: n(width),
        height: n(height)
      };
      assets.set(svg, asset);
    }
    return {
      id,
      type: "furniture",
      x,
      y,
      width: n(width),
      height: n(height),
      layerType: "shapes",
      layerLabel: label,
      color: DEFAULT_FURNITURE_FILL_COLOR,
      furniture: asset
    };
  };
  const makeDesk = (
    s: Primitive,
    original?: import("../types").SpotShape
  ): Shape[] => {
    let o = origin(s),
      rotation = s.rotation || 0;
    let width = s.width,
      height = s.height;
    // The native desk is wide with its chair above the tabletop. Orient the
    // glyph along the table's long axis, rather than stretching it vertically.
    if (height > width) {
      const a = angle(s);
      o = { x: o.x + width * Math.cos(a), y: o.y + width * Math.sin(a) };
      [width, height] = [height, width];
      rotation += 90;
    }
    const a = (rotation * Math.PI) / 180;
    let orientation: "top" | "bottom" =
      original?.orientation === "bottom" ? "bottom" : "top";
    const chairs = (groups.get(s.id) || []).slice(1);
    if (chairs.length) {
      const side =
        chairs.reduce((sum, chair) => {
          const c = center(chair);
          return (
            sum -
            (c.x - o.x) * Math.sin(a) +
            (c.y - o.y) * Math.cos(a) -
            height / 2
          );
        }, 0) / chairs.length;
      if (Math.abs(side) > 1) orientation = side > 0 ? "bottom" : "top";
    } else {
      // If chairs were omitted, a touching pair of tabletops should face away
      // from their shared edge. Only infer this for closely aligned neighbors.
      const c = center(s);
      const paired = anchors
        .filter((t) => t.id !== s.id)
        .map((t) => {
          const p = center(t),
            dx = p.x - c.x,
            dy = p.y - c.y;
          return {
            x: dx * Math.cos(a) + dy * Math.sin(a),
            y: -dx * Math.sin(a) + dy * Math.cos(a),
            table: t
          };
        })
        .filter(
          (p) =>
            Math.abs(
              Math.sin(
                angle(p.table) +
                  (p.table.height > p.table.width ? Math.PI / 2 : 0) -
                  a
              )
            ) < 0.1 &&
            Math.abs(p.x) < width * 0.2 &&
            Math.abs(p.y) > height * 0.7 &&
            Math.abs(p.y) < height * 1.3 &&
            Math.abs(p.table.width * p.table.height - width * height) <
              width * height * 0.2
        )
        .sort((l, r) => Math.abs(l.y) - Math.abs(r.y));
      if (paired.length) orientation = paired[0].y < 0 ? "bottom" : "top";
    }

    rotation = ((((rotation + 180) % 360) + 360) % 360) - 180;
    const scaleY = height / 50;
    const offsetY = orientation === "bottom" ? 10 : 40;
    return [
      {
        ...original,
        id: s.id,
        type: "spot",
        layerType: "spots",
        x: o.x + offsetY * scaleY * Math.sin(a),
        y: o.y - offsetY * scaleY * Math.cos(a),
        width: 90,
        height: 90,
        scaleX: width / 90,
        scaleY,
        rotation,
        spotType: SpotShapeType.Desk,
        spotShape: "custom",
        orientation,
        labelPlacement: "on-rect",
        textAlign: "center",
        rowsExtraSpacing: 0,
        label: original?.label || "",
        name: original?.name || "Imported desk",
        color: DEFAULT_FURNITURE_FILL_COLOR,
        rectProps: { cornerRadius: 2, opacity: 1 }
      }
      // Native desk has its own chair glyph. Keep source chairs available when
      // switching back to a decorative arrangement via the immutable source map.
    ];
  };
  const templates = new Map<string, Shape>();
  const shapes = map.shapes.flatMap((s): Shape[] => {
    if (s.type === "wall-path" || s.type === "wall")
      return [{ ...s, color: DEFAULT_WALL_COLOR }];
    if (owned.has(s.id)) return [];
    if (s.type === "spot" && s.spotType === "desk") {
      const tabletop = nativeDesks.find((t) => t.id === s.id)!;
      return makeDesk(tabletop, s);
    }
    if (
      !primitive(s) ||
      !["table", "chair", "cabinet"].includes(s.layerLabel || "")
    )
      return [s];
    if (s.layerLabel === "table" && deskIds.has(s.id)) return makeDesk(s);
    const arrangement = arrangements.get(s.id)!;
    const { instance, template } = arrangement;
    if (template.count < 2 || template.parts.length === 1) {
      // A single rectangle/ellipse is always cheaper natively, even if repeated.
      // Unique arrangements also remain self-contained: no asset upload/fetch.
      return (groups.get(s.id) || [s]).map((part) => ({
        ...part,
        color: DEFAULT_FURNITURE_FILL_COLOR,
        stroke: "#9CA3AF",
        strokeWidth: 1
      }));
    }
    let shared = templates.get(template.id);
    if (!shared) {
      shared = furniture(
        template.parts,
        template.id,
        s.layerLabel || "Furniture"
      );
      templates.set(template.id, shared);
    }
    return [
      {
        ...shared,
        id: s.id,
        x: instance.x,
        y: instance.y,
        width: n(instance.width),
        height: n(instance.height)
      }
    ];
  });
  return normalizeImportedWallWidths(
    calibrateImportedDesks({ ...map, shapes: fillImportedPillars(shapes) })
  );
}
